import {
  SUPPORTED_DEVICES,
  type SupportedDevice,
} from '@/shared-question-model';
import {
  buildResponderSessionState,
  deviceGateResolved,
  responderSessionReducer,
  DELIVERY_CHANNELS,
  DEVICE_GATE_VERDICTS,
  RESPONSE_MODES,
  type ResponderCapabilities,
  type ResponderSessionState,
  type ResponseMode,
} from '@/shared-responder-v2-core';

import { resolveDeliveryChannel } from './shared-responder-v2-handsfree.helpers';
import {
  isResponderOutroEnabled,
  resolveResponderIntro,
} from './shared-responder-v2-project-chrome.adapter';
import { buildQuestionSpec } from './shared-responder-v2-project-question.adapter';
import {
  DEFAULT_PROJECT_CAN_JUMP_FORWARD,
  DEFAULT_PROJECT_INTERACTION_TYPE,
} from './shared-responder-v2-project.consts';
import type {
  ResponderProjectInput,
  ResponderQuestionInput,
} from './shared-responder-v2-project.types';

/**
 * `isUserVoiceEnabled` is deliberately not consulted. Every manager write site
 * hardcodes it to `true` — `resolveInteractionUpdate` sets it on the text
 * branch as well as the voice one, and `buildPublishModeVoiceSettings` sets it
 * unconditionally — so it records that voice is *permitted*, never that voice
 * is the mode. Reading it here opened a text project's preview in voice.
 *
 * `isAiVoiceEnabled` is the field those same two writers vary with the mode, so
 * it is what a missing `interactionMode` falls back to, paired with
 * `isHandsFree` for a project whose deprecated `mode` was migrated but whose
 * settings were not.
 */
export function resolveProjectInteractionType(args: {
  readonly interactionMode: ResponseMode | null | undefined;
  readonly isAiVoiceEnabled: boolean | null | undefined;
  readonly isHandsFree: boolean | null | undefined;
}): ResponseMode {
  if (args.interactionMode === RESPONSE_MODES.Voice) {
    return RESPONSE_MODES.Voice;
  }

  if (args.interactionMode === RESPONSE_MODES.Text) {
    return RESPONSE_MODES.Text;
  }

  return args.isAiVoiceEnabled || args.isHandsFree
    ? RESPONSE_MODES.Voice
    : DEFAULT_PROJECT_INTERACTION_TYPE;
}

const SUPPORTED_DEVICE_VALUES: readonly string[] =
  Object.values(SUPPORTED_DEVICES);

function isSupportedDeviceType(
  deviceType: string
): deviceType is SupportedDevice {
  return SUPPORTED_DEVICE_VALUES.includes(deviceType);
}

export function toSupportedDeviceTypes(
  deviceTypes: readonly string[] | null | undefined
): readonly SupportedDevice[] {
  return (deviceTypes ?? []).filter(isSupportedDeviceType);
}

export function buildResponderCapabilitiesFromProject(
  project: ResponderProjectInput,
  allowVoice: boolean,
  hostPageUrl: string | null = null
): Omit<ResponderCapabilities, 'hasUrlOrTask'> {
  const projectInteractionType = resolveProjectInteractionType({
    interactionMode: project.interactionMode,
    isAiVoiceEnabled: project.isAiVoiceEnabled,
    isHandsFree: project.isHandsFree,
  });

  return {
    allowVoice,
    canJumpForward: project.canJumpForward ?? DEFAULT_PROJECT_CAN_JUMP_FORWARD,
    deliveryChannel: allowVoice
      ? resolveDeliveryChannel({
          isAiVoiceEnabled: project.isAiVoiceEnabled === true,
          isHandsFree: project.isHandsFree,
          projectInteractionType,
          isInteractionTypeAuthoritative:
            project.isTypelessProject === true &&
            project.interactionMode !== null,
        })
      : DELIVERY_CHANNELS.Chat,
    hasIntro: resolveResponderIntro(project) !== null,
    hasOutro: isResponderOutroEnabled(project),
    hasScreener: project.hasScreener ?? false,
    ...(hostPageUrl === null ? {} : { hostPageUrl }),
    projectInteractionType,
  };
}

/**
 * The session machine opens on `currentStepIndex: 0` and walks the spec list
 * in array order, so the list — not the server's response order — is what
 * decides which question a responder sees first.
 */
function toOrderedQuestions(
  questions: readonly ResponderQuestionInput[]
): readonly ResponderQuestionInput[] {
  return questions.toSorted((a, b) => a.questionOrder - b.questionOrder);
}

/**
 * Opens the gate as `Allowed` because the viewport is unknown server-side;
 * `useResolvedDeviceGate` tightens it after mount.
 */
export function buildResponderSessionStateFromProject(
  project: ResponderProjectInput,
  allowVoice: boolean,
  sessionId?: string,
  hostPageUrl: string | null = null
): ResponderSessionState {
  return responderSessionReducer(
    buildResponderSessionState({
      specs: toOrderedQuestions(project.questions).map((question) =>
        buildQuestionSpec(question, sessionId)
      ),
      capabilities: buildResponderCapabilitiesFromProject(
        project,
        allowVoice,
        hostPageUrl
      ),
    }),
    deviceGateResolved(DEVICE_GATE_VERDICTS.Allowed)
  );
}
